import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { STARTER, CONTACT_EMAIL } from "@/data/temple";
import { Eyebrow, Reveal } from "./Reveal";
import { useSessions, fmtSession } from "./Workshops";
import { API_BASE } from "@/lib/apiBase";

const API = API_BASE;
const INTERESTS = [
  ["starter-guide", "Starter Guide"],
  ["workshop", "The Workshop"],
  ["books", "The Books"],
  ["all", "All of it"],
];
const field =
  "w-full bg-transparent border-b border-[#D9B47A]/30 py-4 text-lg text-[#F2E8DF] placeholder:text-[#8F8176] focus:outline-none focus:border-[#D9B47A] transition-colors duration-300";

export default function Enter() {
  const [form, setForm] = useState({ name: "", email: "", interest: "starter-guide", message: "", session_id: null });
  const [sending, setSending] = useState(false);
  const sessions = useSessions();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  useEffect(() => {
    const pick = (e) => setForm((f) => ({ ...f, interest: "workshop", session_id: e.detail ?? null }));
    window.addEventListener("tt-select-session", pick);
    return () => window.removeEventListener("tt-select-session", pick);
  }, []);
  const showSessions = sessions.length > 0 && (form.interest === "workshop" || form.interest === "all");

  const submit = async (e) => {
    e.preventDefault();
    if (!API) {
      window.location.href = `mailto:${CONTACT_EMAIL}`;
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, session_id: showSessions ? form.session_id : null });
      toast.success(
        form.interest === "starter-guide" || form.interest === "all"
          ? `Beloved ${form.name.split(" ")[0]}, your Starter Guide is on its way to your inbox.`
          : `Beloved ${form.name.split(" ")[0]}, your request has been received.`
      );
      setForm({ name: "", email: "", interest: "starter-guide", message: "", session_id: null });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : `Something interrupted the current. Please email ${CONTACT_EMAIL}.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="enter" data-testid="enter-section" className="relative bg-[#0D0908] border-t border-[#D9B47A]/10">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10 py-28 sm:py-40 grid lg:grid-cols-12 gap-16">
        <Reveal className="lg:col-span-5">
          <Eyebrow n="X">Enter the Living Temple</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-8 leading-[1.05]">
            The book gives you the map. The workshop gives you the <em className="text-[#D9B47A]">experience.</em>
          </h2>
          <p className="mt-8 text-[#CFC2B6] leading-relaxed">Begin with a free gift — The Tantric Temple Starter Guide:</p>
          <ol className="mt-6 space-y-4">
            {STARTER.map((s, i) => (
              <li key={s} className="flex gap-5 text-[15px] text-[#CFC2B6]">
                <span className="font-tt-mono text-[12px] text-[#D9B47A] pt-1">{String(i + 1).padStart(2, "0")}</span>
                {s}
              </li>
            ))}
          </ol>
          <a
            data-testid="enter-email-link"
            href={`mailto:${CONTACT_EMAIL}`}
            className="inline-flex items-center gap-2 mt-10 font-display italic text-xl text-[#D9B47A] hover:text-[#F2E8DF] transition-colors"
          >
            {CONTACT_EMAIL} <ArrowUpRight className="w-4 h-4" />
          </a>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-6 lg:col-start-7">
          <form data-testid="enter-form" onSubmit={submit} className="space-y-6">
            <input data-testid="enter-input-name" required maxLength={120} value={form.name} onChange={set("name")} placeholder="Your name" className={field} />
            <input data-testid="enter-input-email" required type="email" value={form.email} onChange={set("email")} placeholder="Your email" className={field} />
            <div className="pt-4">
              <p className="font-tt-mono text-[12px] uppercase tracking-[0.25em] text-[#A8998C] mb-4">I am called to</p>
              <div className="flex flex-wrap gap-2.5">
                {INTERESTS.map(([k, l]) => (
                  <button
                    type="button"
                    key={k}
                    data-testid={`enter-interest-${k}`}
                    onClick={() => setForm((f) => ({ ...f, interest: k }))}
                    className={`rounded-full border px-5 py-2.5 text-sm transition-colors duration-300 ${
                      form.interest === k ? "bg-[#D9B47A] border-[#D9B47A] text-[#0A0706]" : "border-[#D9B47A]/30 text-[#CFC2B6] hover:border-[#D9B47A]"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {showSessions && (
              <div className="pt-2" data-testid="enter-sessions">
                <p className="font-tt-mono text-[12px] uppercase tracking-[0.25em] text-[#A8998C] mb-4">Which session?</p>
                <div className="flex flex-wrap gap-2.5">
                  {[{ id: null, title: "Any upcoming date" }, ...sessions].map((s) => (
                    <button
                      type="button"
                      key={s.id ?? "any"}
                      data-testid={`enter-session-${s.id ?? "any"}`}
                      onClick={() => setForm((f) => ({ ...f, session_id: s.id }))}
                      className={`rounded-full border px-5 py-2.5 text-sm transition-colors duration-300 ${form.session_id === s.id ? "bg-[#C2414F] border-[#C2414F] text-[#F2E8DF]" : "border-[#D9B47A]/30 text-[#CFC2B6] hover:border-[#D9B47A]"}`}
                    >
                      {s.id ? `${s.title} · ${fmtSession(s.starts_at).day} ${fmtSession(s.starts_at).month}` : s.title}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <textarea data-testid="enter-input-message" maxLength={2000} rows={3} value={form.message} onChange={set("message")} placeholder="Where are you on the path? (optional)" className={`${field} resize-none`} />
            <button
              data-testid="enter-submit-btn"
              disabled={sending}
              className="group mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-full bg-[#C2414F] px-10 py-5 font-tt-mono text-[12px] uppercase tracking-[0.25em] text-[#F2E8DF] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors duration-300 disabled:opacity-60"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Request my invitation
              <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
            </button>
            <p className="text-[13px] text-[#A8998C]">Your details are held in confidence and used only to reply to you.</p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
